/**
 * UserSelect Component
 *
 * A standardized dropdown for selecting users/team members.
 * Features avatar display, alphabetical sorting, and consistent styling.
 *
 * @example
 * ```jsx
 * <UserSelect
 *   value={assignedTo}
 *   onChange={(userId) => setAssignedTo(userId)}
 *   users={team?.rows}
 *   placeholder="Unassigned"
 * />
 * ```
 */

import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { AvatarGenerated } from '../Avatar';
import { classNames, getUserFullName } from '../helpers/functions';

/**
 * Get display name for a user, handling different field name formats
 */
const getDisplayName = (user) => {
	if (!user) return '';
	const firstName = user.firstName || user.first_name || '';
	const lastName = user.lastName || user.last_name || '';
	return [firstName, lastName].filter(Boolean).join(' ') || user.email || 'Unknown';
};

/**
 * Sort users alphabetically by display name
 */
const sortUsers = (users) => {
	if (!Array.isArray(users)) return [];
	return [...users].sort((a, b) => {
		const nameA = getDisplayName(a).toLowerCase();
		const nameB = getDisplayName(b).toLowerCase();
		return nameA.localeCompare(nameB);
	});
};

/**
 * Filter to only active, non-archived users
 */
const filterActiveUsers = (users) => {
	if (!Array.isArray(users)) return [];
	return users.filter((user) => user.active !== false && !user.archived);
};

const UserSelect = ({
	value,
	onChange,
	users = [],
	placeholder = 'Unassigned',
	label,
	disabled = false,
	showUnassigned = true,
	filterActive = true,
	className = '',
	size = 'md', // 'sm', 'md', 'lg'
	variant = 'default', // 'default', 'inline'
}) => {
	// Process users: filter active and sort alphabetically
	const processedUsers = sortUsers(filterActive ? filterActiveUsers(users) : users);

	// Find selected user
	const selectedUser = processedUsers.find((u) => u.id === value);

	// Size classes
	const sizeClasses = {
		sm: {
			button: 'py-1.5 pl-2 pr-8 text-xs',
			option: 'py-1.5 pl-2 pr-8 text-xs',
			avatar: 'xxs',
			check: 'h-3 w-3',
			chevron: 'h-4 w-4',
		},
		md: {
			button: 'py-2 pl-3 pr-10 text-sm',
			option: 'py-2 pl-3 pr-9 text-sm',
			avatar: 'xxs',
			check: 'h-4 w-4',
			chevron: 'h-5 w-5',
		},
		lg: {
			button: 'py-2.5 pl-3 pr-10 text-base',
			option: 'py-2.5 pl-3 pr-9 text-base',
			avatar: 'xs',
			check: 'h-5 w-5',
			chevron: 'h-5 w-5',
		},
	};

	const sizes = sizeClasses[size] || sizeClasses.md;

	// Variant styles
	const isInline = variant === 'inline';

	const buttonClasses = isInline
		? classNames(
				'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-pointer',
				'bg-gray-100 text-gray-700 hover:bg-gray-200',
			)
		: classNames(
				'relative w-full cursor-pointer rounded-lg bg-white text-left border border-gray-300',
				'focus:outline-none focus:ring-2 focus:ring-indigo-500',
				sizes.button,
			);

	return (
		<div className={className}>
			{label && (
				<label className="block text-sm font-medium text-gray-700 mb-1">
					{label}
				</label>
			)}
			<Listbox value={value || ''} onChange={onChange} disabled={disabled}>
				<div className="relative">
					<Listbox.Button className={buttonClasses}>
						<span className={classNames('flex items-center', !isInline && 'truncate')}>
							{selectedUser ? (
								<>
									<AvatarGenerated
										id={getDisplayName(selectedUser)}
										size={sizes.avatar}
										className="mr-2 flex-shrink-0"
									/>
									<span className="truncate">{getDisplayName(selectedUser)}</span>
								</>
							) : (
								<span className={isInline ? '' : 'text-gray-500'}>{placeholder}</span>
							)}
						</span>
						{!isInline && (
							<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
								<ChevronUpDownIcon className={classNames(sizes.chevron, 'text-gray-400')} />
							</span>
						)}
					</Listbox.Button>

					<Listbox.Options
						className={classNames(
							'absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg',
							'ring-1 ring-black ring-opacity-5 focus:outline-none',
							isInline ? 'w-56 right-0' : 'w-full',
						)}
					>
						{showUnassigned && (
							<Listbox.Option
								value=""
								className={({ active, selected }) =>
									classNames(
										'cursor-pointer select-none relative',
										sizes.option,
										active ? 'bg-indigo-50' : '',
										selected ? 'bg-indigo-100' : '',
									)
								}
							>
								{({ selected }) => (
									<div className="flex items-center">
										<span
											className={classNames(
												'text-gray-500',
												selected && 'font-semibold',
											)}
										>
											{placeholder}
										</span>
										{selected && (
											<CheckIcon
												className={classNames(sizes.check, 'text-indigo-600 ml-auto')}
											/>
										)}
									</div>
								)}
							</Listbox.Option>
						)}

						{processedUsers.map((user) => (
							<Listbox.Option
								key={user.id}
								value={user.id}
								className={({ active, selected }) =>
									classNames(
										'cursor-pointer select-none relative',
										sizes.option,
										active ? 'bg-indigo-50' : '',
										selected ? 'bg-indigo-100' : '',
									)
								}
							>
								{({ selected }) => (
									<div className="flex items-center">
										<AvatarGenerated
											id={getDisplayName(user)}
											size={sizes.avatar}
											className="mr-2 flex-shrink-0"
										/>
										<span
											className={classNames('truncate', selected && 'font-semibold')}
										>
											{getDisplayName(user)}
										</span>
										{selected && (
											<CheckIcon
												className={classNames(sizes.check, 'text-indigo-600 ml-auto')}
											/>
										)}
									</div>
								)}
							</Listbox.Option>
						))}

						{processedUsers.length === 0 && (
							<div className="py-2 px-3 text-sm text-gray-500">No users available</div>
						)}
					</Listbox.Options>
				</div>
			</Listbox>
		</div>
	);
};

export default UserSelect;
